import type { Locale } from "@commerce-frontend/i18n/types";
import type {
	ProductDetailPageFragmentFragment,
	ProductVariantFragmentFragment,
} from "@commerce-frontend/types";
import { isValue } from "@labdigital/toolkit";
import type { Product, WithContext } from "schema-dts";
import { useStoreConfig } from "../store-config/context";

type Props = {
	product: ProductDetailPageFragmentFragment;
	variant: ProductVariantFragmentFragment;
	locale: Locale;
};

export const ProductDetailPageJsonLd = ({ product, variant, locale }: Props) => {
	const price = product.primaryVariant.regularPrice?.gross.centAmount;
	const sku = product.slug.split("-").pop();

	const config = useStoreConfig();
	const jsonLd: WithContext<Product> = {
		"@context": "https://schema.org",
		"@type": "Product",
		sku,
		name: product.name ?? "",
		image: variant.images ? variant.images.map((i) => i.url ?? "").filter(isValue) : [],
		offers: {
			"@type": "Offer",
			priceCurrency: "EUR",
			price: price ? formatCurrency(price) : "",
			itemCondition: "https://schema.org/NewCondition",
			availability:
				product.availability === "IN_STOCK"
					? "https://schema.org/InStock"
					: "https://schema.org/OutOfStock",
			url: `${config.metadata.productionUrl}${locale.toLocaleLowerCase()}/product/${product.slug}`,
		},
		...(product.reviews?.amount
			? {
					aggregateRating: {
						"@type": "AggregateRating",
						ratingValue: product.reviews?.averageRating,
						ratingCount: product.reviews?.amount,
						bestRating: product.reviews?.highestRating,
						worstRating: product.reviews?.lowestRating,
					},
				}
			: {}),
	};

	return (
		<script
			type="application/ld+json"
			dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
		/>
	);
};

const formatCurrency = (number: number) => (number / 100).toFixed(2);
